import { startFederatedLogin } from 'korint-sdk';
import { useKorintConfig } from 'korint-sdk/react';
import { Loader2Icon } from 'lucide-react';
import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Center } from '@folio/components/ui/flex';
import { buttonVariants } from '../../../ui/button';
import { AuthLayout } from '../auth-layout';

/** Every value the refusal page can explain; anything else gets the generic text. */
const REFUSAL_REASONS = [
  'not_configured',
  'disabled',
  'invalid_flow',
  'identity_provider_error',
  'scopes_too_long',
  'token_exchange_failed',
  'invalid_id_token',
  'wrong_provider',
  'stale_authentication',
  'invalid_claims',
  'missing_intermediary',
  'missing_distributor',
  'missing_account',
  'unknown_network',
  'unknown_role',
  'no_entitled_product',
  'missing_intermediary_email',
  'broker_removed',
  'firm_mismatch',
  'role_mismatch',
  'replayed_token',
  'unavailable',
  'renewal_failed',
  'storage_unavailable',
  'signed_out',
  'sign_in_with_portal',
] as const;

type RefusalReason = (typeof REFUSAL_REASONS)[number];

const isRefusalReason = (value: string | null): value is RefusalReason =>
  REFUSAL_REASONS.includes(value as RefusalReason);

const REFERENCE_PATTERN = /^[0-9a-zA-Z-]{8,64}$/;

/** Where Alptis Connect (or any provider portal) links to; also the renewal entry. */
export const FederatedEntryView = () => {
  const [searchParameters] = useSearchParams();
  const { clientOptions } = useKorintConfig();
  const startUrl = clientOptions.federatedLogin?.startUrl;

  useEffect(() => {
    if (startUrl) {
      const loginHint = searchParameters.get('login_hint');
      startFederatedLogin({
        startUrl,
        target: searchParameters.get('target') ?? '/',
        ...(loginHint ? { loginHint } : {}),
      });
    }
  }, [startUrl, searchParameters]);

  return (
    <Center className="fixed inset-0">
      <Loader2Icon className="w-8 h-8 animate-spin" />
    </Center>
  );
};

export const FederatedRefusalView = ({ logo }: { logo: ReactNode }) => {
  const { t } = useTranslation();
  const [searchParameters] = useSearchParams();
  const { clientOptions } = useKorintConfig();
  const portalUrl = clientOptions.federatedLogin?.portalUrl;

  const requested = searchParameters.get('reason');
  const reason = isRefusalReason(requested) ? requested : 'generic';
  const reference = searchParameters.get('reference');
  const isSignOut = reason === 'signed_out';

  return (
    <AuthLayout
      title={t(isSignOut ? 'federatedLogin.signedOutTitle' : 'federatedLogin.refusedTitle')}
      logo={logo}
    >
      <p>{t(`federatedLogin.reasons.${reason}`)}</p>
      {reference && REFERENCE_PATTERN.test(reference) ? (
        <p className="pt-4 text-sm text-gray-500">{t('federatedLogin.reference', { reference })}</p>
      ) : null}
      {portalUrl ? (
        <div className="pt-8">
          <a href={portalUrl} className={buttonVariants({ className: 'w-full' })}>
            {t('federatedLogin.backToPortal')}
          </a>
        </div>
      ) : null}
    </AuthLayout>
  );
};
